import { useEffect, useMemo, useState } from 'react'
import { Wrench } from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Switch } from '../../lib/shadcn/switch'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import {
  useAttachIncidentEvidence,
  useGetIncidentFormOptions,
  useRecordIncident,
} from '../../hooks/backend/fleet'
import { writeErrorMessage } from '../../utils/writeError'
import { type FleetRow, LOCATION_OPTIONS } from '../../utils/fleet'
import {
  type IncidentFormVehicle, type IncidentFormDriver, type TriStateValue,
  DRIVABLE_OPTIONS, INCIDENT_FORM_TYPES, INCIDENT_STATUSES, INFORMED_OPTIONS,
  SEVERITY_OPTIONS, SUB_TYPE_OPTIONS, URGENCY_OPTIONS, triStatePayload,
} from '../../utils/servicing'
import { NotifyBox, TriState, UnknownToggle } from './IncidentControls'
import { IncidentEvidence, type UploadedEvidence } from './IncidentEvidence'

/**
 * Log a repair or an incident.
 *
 * This replaces the ten-field dialog that shipped with the servicing work. That
 * form could not express the report the business actually runs on: the Vehicle
 * Incident Report Typeform asks twenty-three questions across five branches, and
 * an incident filed from the Cockpit was a thinner record than the same incident
 * filed on a phone. Both now write through record_cockpit_incident_v1.
 *
 * The trigger button is unchanged on purpose. It is the affordance fleet staff
 * already reach for on the Fleet list.
 *
 * Used two ways. With `vehicle`, from a Fleet row, where the vehicle is fixed.
 * Without it, from the Vehicle Incidents page, where the vehicle and the driver
 * are chosen.
 */
export function IncidentFormDialog({
  vehicle,
  onSaved,
  triggerLabel,
  triggerSize = 'sm',
  hideTrigger = false,
  controlledOpen,
  onControlledOpenChange,
  seedOperational,
  seedLocation,
  onRecorded,
}: {
  vehicle?: FleetRow | null
  onSaved: () => void
  triggerLabel?: string
  triggerSize?: 'sm' | 'default'
  /** Opened by another screen rather than by its own button. */
  hideTrigger?: boolean
  controlledOpen?: boolean
  onControlledOpenChange?: (open: boolean) => void
  /** What the caller was about to set, so the report starts from that answer. */
  seedOperational?: boolean
  seedLocation?: string
  /**
   * Called instead of onSaved when the caller needs the incident id back, which
   * is how the Edit vehicle gate links a car coming off the road to its report.
   */
  onRecorded?: (incidentId: string | null) => void
}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const controlled = controlledOpen !== undefined
  const open = controlled ? controlledOpen : internalOpen
  const setOpen = (next: boolean) => {
    if (controlled) onControlledOpenChange?.(next)
    else setInternalOpen(next)
  }
  const options = useGetIncidentFormOptions()
  const record = useRecordIncident()
  const attach = useAttachIncidentEvidence()
  const optionsTrigger = options.trigger

  useEffect(() => {
    if (open) optionsTrigger({}, { skipCache: true })
  }, [open, optionsTrigger])

  const data = options.data as
    | { vehicles: IncidentFormVehicle[]; drivers: IncidentFormDriver[] }
    | undefined
  const vehicles = data?.vehicles ?? []
  const drivers = data?.drivers ?? []

  const [vehicleId, setVehicleId] = useState('')
  const [driverId, setDriverId] = useState('')
  const [incidentType, setIncidentType] = useState('')
  const [subType, setSubType] = useState('')
  const [severity, setSeverity] = useState('')
  const [occurredOn, setOccurredOn] = useState('')
  const [incidentTime, setIncidentTime] = useState('')
  const [locationText, setLocationText] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [urgency, setUrgency] = useState('')
  const [operational, setOperational] = useState(true)
  const [currentLocation, setCurrentLocation] = useState('')
  const [moveTo, setMoveTo] = useState('')
  const [nextUpdate, setNextUpdate] = useState('')
  const [informed, setInformed] = useState<string[]>([])
  const [firstKnowledge, setFirstKnowledge] = useState('')
  const [context, setContext] = useState('')
  const [evidence, setEvidence] = useState('')
  const [files, setFiles] = useState<UploadedEvidence[]>([])
  const [status, setStatus] = useState('open')
  const [notify, setNotify] = useState(true)

  // The three fields that cannot honestly be known when a report is written at
  // the roadside. Required like everything else, but answerable as "not known".
  const [caseUnknown, setCaseUnknown] = useState(false)
  const [estimateUnknown, setEstimateUnknown] = useState(false)
  const [storageUnknown, setStorageUnknown] = useState(false)
  const [costUnknown, setCostUnknown] = useState(false)
  const [mileageUnknown, setMileageUnknown] = useState(false)

  const [injuries, setInjuries] = useState<TriStateValue>('unset')
  const [otherVehicles, setOtherVehicles] = useState<TriStateValue>('unset')
  const [police, setPolice] = useState<TriStateValue>('unset')
  const [caseNumber, setCaseNumber] = useState('')
  const [drivable, setDrivable] = useState('')
  const [estimatedCost, setEstimatedCost] = useState('')
  const [dailyStorage, setDailyStorage] = useState('')
  const [mileage, setMileage] = useState('')
  const [cost, setCost] = useState('')
  const [impound, setImpound] = useState('')

  const fixed = vehicle ?? null
  const activeVehicleId = fixed?.vehicle_id ?? vehicleId

  const selected = useMemo(
    () => vehicles.find((v) => v.vehicle_id === activeVehicleId) ?? null,
    [vehicles, activeVehicleId],
  )

  // The operational switch writes straight back to vehicles.operational, and the
  // weekly allocation pools on that column. Defaulting it to "on" for a vehicle
  // nobody has looked at would put a damaged car back in the pool, so it always
  // starts from what the register already says. It re-runs when the option list
  // arrives, because the list loads after the dialog opens.
  useEffect(() => {
    if (fixed) return
    if (!activeVehicleId) return
    setOperational(selected?.operational === true)
  }, [fixed, activeVehicleId, selected])

  function reset() {
    setVehicleId('')
    setDriverId('')
    setIncidentType('')
    setSubType('')
    setSeverity('')
    setOccurredOn(new Date().toISOString().slice(0, 10))
    setIncidentTime('')
    setLocationText('')
    setTitle('')
    setDescription('')
    setUrgency('')
    setOperational(seedOperational ?? (fixed ? fixed.operational === true : true))
    setCurrentLocation(seedLocation ?? fixed?.location ?? '')
    setMoveTo('')
    setNextUpdate('')
    setInformed([])
    setFirstKnowledge('')
    setContext('')
    setEvidence('')
    setFiles([])
    setStatus('open')
    setNotify(true)
    setCaseUnknown(false)
    setEstimateUnknown(false)
    setStorageUnknown(false)
    setCostUnknown(false)
    setMileageUnknown(false)
    setInjuries('unset')
    setOtherVehicles('unset')
    setPolice('unset')
    setCaseNumber('')
    setDrivable('')
    setEstimatedCost('')
    setDailyStorage('')
    setMileage('')
    setCost('')
    setImpound('')
  }

  // Choosing the Licensing branch means the car has been taken, so its location
  // defaults to Impounded. It is a default and not a lock: a licensing matter can
  // end with the car at the office, and the operator can say so.
  useEffect(() => {
    if (incidentType !== 'Licensing / Compliance') return
    setCurrentLocation((prev) => (prev === '' || prev === (fixed?.location ?? '') ? 'Impounded' : prev))
  }, [incidentType, fixed])

  const isAccident = incidentType === 'Accident'
  const isMechanical = incidentType === 'Mechanical Issue'
  const isService = incidentType === 'Service / Maintenance'
  const isLicensing = incidentType === 'Licensing / Compliance'
  const subTypes = SUB_TYPE_OPTIONS[incidentType] ?? []
  const showSubType = subTypes.length > 0
  const showDrivable = isAccident || isMechanical
  const showEstimated = isAccident || isLicensing
  const showStorage = isAccident || isLicensing

  const onThisVehicle = activeVehicleId
    ? drivers.filter((d) => d.current_vehicle_id === activeVehicleId)
    : []
  const otherDrivers = activeVehicleId
    ? drivers.filter((d) => d.current_vehicle_id !== activeVehicleId)
    : drivers

  // Every field on the chosen branch is required. The three that cannot be known
  // at the roadside are satisfied either by a value or by "Not known yet", which
  // is a recorded answer rather than a blank.
  const missing: string[] = []
  const need = (ok: boolean, label: string) => { if (!ok) missing.push(label) }

  need(!!activeVehicleId, 'Vehicle')
  need(driverId !== '', 'Driver')
  need(incidentType !== '', 'Incident type')
  need(severity !== '', 'Severity')
  need(occurredOn !== '', 'Date of incident')
  need(incidentTime.trim() !== '', 'Time of incident')
  need(locationText.trim() !== '', 'Location of incident')
  need(title.trim() !== '', 'Title')
  need(description.trim() !== '', 'Description')
  need(urgency !== '', 'Urgency level')
  need(nextUpdate !== '', 'Next update date')
  need(firstKnowledge !== '', 'When did you become aware')
  need(informed.length > 0, 'Who has been informed')
  need(context.trim() !== '', 'Context and next steps')
  need(currentLocation.trim() !== '', 'Current location')
  if (isLicensing) need(impound.trim() !== '', 'Which pound')

  if (showSubType) need(subType !== '', isService ? 'Service type' : 'Issue type')
  if (showDrivable) need(drivable !== '', 'Drivable')
  if (showEstimated) need(estimateUnknown || estimatedCost.trim() !== '', 'Estimated cost')
  if (showStorage) need(storageUnknown || dailyStorage.trim() !== '', 'Daily storage cost')
  if (isAccident) {
    need(injuries !== 'unset', 'Injuries')
    need(otherVehicles !== 'unset', 'Other vehicles involved')
    need(police !== 'unset', 'Police on scene')
    need(caseUnknown || caseNumber.trim() !== '', 'Case number')
  }
  if (isService) {
    need(mileageUnknown || mileage.trim() !== '', 'Mileage')
    need(costUnknown || cost.trim() !== '', 'Cost')
  }

  const canSave = missing.length === 0 && !record.loading && !attach.loading

  function toggleInformed(v: string) {
    setInformed((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
  }

  function nullIfEmpty(v: string): string | null {
    const t = v.trim()
    return t === '' ? null : t
  }
  function numOrNull(v: string): number | null {
    const t = v.trim()
    if (t === '') return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  }

  async function handleSave() {
    const urls = evidence.split('\n').map((s) => s.trim()).filter((s) => s !== '')
    const chosenDriver = drivers.find((d) => d.driver_id === driverId) ?? null

    const payload: Record<string, unknown> = {
      vehicle_id: activeVehicleId,
      incident_type: incidentType,
      severity: nullIfEmpty(severity),
      title: nullIfEmpty(title),
      // The pound's name has no column of its own. Until it earns one it is
      // carried as a labelled line on the description, which is read on the
      // incident and reaches the owner, rather than being written into
      // current_location, which every other screen treats as a closed list. That
      // is exactly how 'On Fleet Johannesburg' got into the register.
      description: nullIfEmpty(
        isLicensing && impound.trim() !== ''
          ? `${description.trim()}\n\nImpound yard: ${impound.trim()}`
          : description,
      ),
      occurred_at: nullIfEmpty(occurredOn),
      incident_time: nullIfEmpty(incidentTime),
      location_text: nullIfEmpty(locationText),
      current_location: nullIfEmpty(currentLocation),
      move_to: nullIfEmpty(moveTo),
      urgency: nullIfEmpty(urgency),
      vehicle_operational: operational,
      context_next_steps: nullIfEmpty(context),
      first_knowledge: nullIfEmpty(firstKnowledge),
      informed: informed.length > 0 ? informed.join(', ') : null,
      evidence_urls: urls.length > 0 ? urls : null,
      next_update_date: nullIfEmpty(nextUpdate),
      status,
      notify,
    }
    // '__none__' is an explicit answer, not a blank. It carries no driver_id, so
    // nothing is attached to a person who was not there.
    if (driverId && driverId !== '__none__') {
      payload.driver_id = driverId
      payload.driver_name = chosenDriver?.driver_name ?? null
    }
    if (showSubType) payload.sub_type = nullIfEmpty(subType)
    if (showDrivable) payload.drivable = nullIfEmpty(drivable)
    if (showEstimated) payload.estimated_cost_zar = estimateUnknown ? null : numOrNull(estimatedCost)
    if (showStorage) payload.daily_storage_cost_zar = storageUnknown ? null : numOrNull(dailyStorage)
    if (isAccident) {
      const inj = triStatePayload(injuries)
      const other = triStatePayload(otherVehicles)
      const pol = triStatePayload(police)
      if (inj) payload.injuries = inj
      if (other) payload.other_vehicles = other
      if (pol) payload.police_on_scene = pol
      payload.case_number = caseUnknown ? 'Not known yet' : nullIfEmpty(caseNumber)
    }
    if (isService) {
      payload.odometer_km = mileageUnknown ? null : numOrNull(mileage)
      payload.cost_zar = costUnknown ? null : numOrNull(cost)
    }

    try {
      const result = (await record.trigger({ payload }).result) as
        | {
            incident_id?: string
            notification?: { recipient?: string | null; queued?: boolean } | null
          }
        | null

      // The files were catalogued against the vehicle before the incident
      // existed, because an upload needs a vehicle and the incident needs a
      // complete form. Claim them now. A failure here must not read as a failure
      // to record the incident, which is already saved.
      const incidentId = result?.incident_id
      if (incidentId && files.length > 0) {
        try {
          await attach.trigger({
            incident_id: incidentId,
            document_ids: files.map((f) => f.document_id),
          }).result
        } catch (err) {
          toast.error('The incident saved, but the evidence did not attach', {
            description: writeErrorMessage(err),
          })
        }
      }

      const recipient = result?.notification?.queued ? result.notification?.recipient : null
      toast.success('Incident recorded', {
        description: recipient
          ? `Report queued to ${recipient}.`
          : 'Recorded. No report was sent.',
      })
      setOpen(false)
      // onRecorded owns the follow-up when a caller needs the incident id, and
      // calling onSaved as well would reload the list twice and race the patch
      // that caller is about to send.
      if (onRecorded) onRecorded(incidentId ?? null)
      else onSaved()
    } catch (err) {
      toast.error('Could not record the incident', { description: writeErrorMessage(err) })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reset()
        setOpen(next)
      }}
    >
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size={triggerSize}
            className={cn('gap-1.5', triggerSize === 'sm' && 'h-7 px-2 text-xs')}
          >
            <Wrench className={triggerSize === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            {triggerLabel ?? 'Log repair/incident'}
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log repair / incident</DialogTitle>
          <DialogDescription>
            {fixed
              ? `${fixed.registration_number || 'Vehicle'} · ${[fixed.make, fixed.model].filter(Boolean).join(' ') || 'Unknown vehicle'}`
              : 'The same questions the incident report form asks.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          {!fixed && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="inc-vehicle">
                Vehicle <span className="text-destructive">*</span>
              </Label>
              <NativeSelect
                id="inc-vehicle"
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
              >
                <option value="">
                  {options.loading ? 'Loading vehicles…' : 'Choose a vehicle'}
                </option>
                {vehicles.map((v) => (
                  <option key={v.vehicle_id} value={v.vehicle_id}>
                    {v.label ?? v.registration_number ?? 'Vehicle'}
                    {v.owner_name ? ` · ${v.owner_name}` : ''}
                  </option>
                ))}
              </NativeSelect>
            </div>
          )}

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="inc-driver">Driver <span className="text-destructive">*</span></Label>
            <NativeSelect
              id="inc-driver"
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
            >
              <option value="">Choose a driver</option>
              <option value="__none__">No driver involved</option>
              {onThisVehicle.length > 0 && (
                <optgroup label="On this vehicle">
                  {onThisVehicle.map((d) => (
                    <option key={d.driver_id} value={d.driver_id}>
                      {d.driver_name ?? 'Driver'}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label={onThisVehicle.length > 0 ? 'All other active drivers' : 'Active drivers'}>
                {otherDrivers.map((d) => (
                  <option key={d.driver_id} value={d.driver_id}>
                    {d.driver_name ?? 'Driver'}
                    {d.current_registration ? ` · ${d.current_registration}` : ''}
                  </option>
                ))}
              </optgroup>
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-type">
              Incident type <span className="text-destructive">*</span>
            </Label>
            <NativeSelect
              id="inc-type"
              value={incidentType}
              onChange={(e) => {
                setIncidentType(e.target.value)
                setSubType('')
              }}
            >
              <option value="">Choose a type</option>
              {INCIDENT_FORM_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-severity">Severity <span className="text-destructive">*</span></Label>
            <NativeSelect id="inc-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">Choose one</option>
              {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-date">Date of incident <span className="text-destructive">*</span></Label>
            <Input id="inc-date" type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inc-time">Time of incident <span className="text-destructive">*</span></Label>
            <Input id="inc-time" value={incidentTime} onChange={(e) => setIncidentTime(e.target.value)} placeholder="e.g. around 20:00" />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="inc-where">Location of incident <span className="text-destructive">*</span></Label>
            <Input id="inc-where" value={locationText} onChange={(e) => setLocationText(e.target.value)} placeholder="Where it happened" />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="inc-title">Title <span className="text-destructive">*</span></Label>
            <Input id="inc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short summary" />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="inc-desc">
              Description <span className="text-destructive">*</span>
            </Label>
            <Textarea id="inc-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What happened" />
          </div>

          {showSubType && (
            <div className="space-y-1.5">
              <Label htmlFor="inc-subtype">{isService ? 'Service type' : 'Issue type'} <span className="text-destructive">*</span></Label>
              <NativeSelect id="inc-subtype" value={subType} onChange={(e) => setSubType(e.target.value)}>
                <option value="">Choose one</option>
                {subTypes.map((s) => <option key={s} value={s}>{s}</option>)}
              </NativeSelect>
            </div>
          )}

          {showDrivable && (
            <div className="space-y-1.5">
              <Label htmlFor="inc-drivable">Drivable <span className="text-destructive">*</span></Label>
              <NativeSelect id="inc-drivable" value={drivable} onChange={(e) => setDrivable(e.target.value)}>
                <option value="">Choose one</option>
                {DRIVABLE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </NativeSelect>
            </div>
          )}

          {isAccident && (
            <>
              <TriState id="inc-injuries" label="Injuries" value={injuries} onChange={setInjuries} allowUnset={false} required />
              <TriState id="inc-others" label="Other vehicles involved" value={otherVehicles} onChange={setOtherVehicles} allowUnset={false} required />
              <TriState id="inc-police" label="Police on scene" value={police} onChange={setPolice} allowUnset={false} required />
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="inc-case">
                    Case number <span className="text-destructive">*</span>
                  </Label>
                  <UnknownToggle id="inc-case-unknown" unknown={caseUnknown} onChange={setCaseUnknown} />
                </div>
                <Input
                  id="inc-case"
                  value={caseUnknown ? '' : caseNumber}
                  disabled={caseUnknown}
                  placeholder={caseUnknown ? 'Recorded as not known yet' : ''}
                  onChange={(e) => setCaseNumber(e.target.value)}
                />
              </div>
            </>
          )}

          {isService && (
            <>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="inc-mileage">
                    Mileage (km) <span className="text-destructive">*</span>
                  </Label>
                  <UnknownToggle id="inc-mileage-unknown" unknown={mileageUnknown} onChange={setMileageUnknown} />
                </div>
                <Input id="inc-mileage" type="number" disabled={mileageUnknown}
                  value={mileageUnknown ? '' : mileage} onChange={(e) => setMileage(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label htmlFor="inc-cost">
                    Cost (ZAR) <span className="text-destructive">*</span>
                  </Label>
                  <UnknownToggle id="inc-cost-unknown" unknown={costUnknown} onChange={setCostUnknown} />
                </div>
                <Input id="inc-cost" type="number" disabled={costUnknown}
                  value={costUnknown ? '' : cost} onChange={(e) => setCost(e.target.value)} />
              </div>
            </>
          )}

          {showEstimated && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="inc-est">
                  Estimated cost (ZAR) <span className="text-destructive">*</span>
                </Label>
                <UnknownToggle id="inc-est-unknown" unknown={estimateUnknown} onChange={setEstimateUnknown} />
              </div>
              <Input id="inc-est" type="number" disabled={estimateUnknown}
                value={estimateUnknown ? '' : estimatedCost} onChange={(e) => setEstimatedCost(e.target.value)} />
            </div>
          )}
          {showStorage && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="inc-storage">
                  Daily storage cost (ZAR) <span className="text-destructive">*</span>
                </Label>
                <UnknownToggle id="inc-storage-unknown" unknown={storageUnknown} onChange={setStorageUnknown} />
              </div>
              <Input id="inc-storage" type="number" disabled={storageUnknown}
                value={storageUnknown ? '' : dailyStorage} onChange={(e) => setDailyStorage(e.target.value)} />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="inc-urgency">Urgency level <span className="text-destructive">*</span></Label>
            <NativeSelect id="inc-urgency" value={urgency} onChange={(e) => setUrgency(e.target.value)}>
              <option value="">Choose one</option>
              {URGENCY_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
            </NativeSelect>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inc-status">Status</Label>
            <NativeSelect id="inc-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {INCIDENT_STATUSES.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-curloc">Current location <span className="text-destructive">*</span></Label>
            <NativeSelect id="inc-curloc" value={currentLocation} onChange={(e) => setCurrentLocation(e.target.value)}>
              <option value="">Choose a location</option>
              {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              {currentLocation !== '' && !LOCATION_OPTIONS.includes(currentLocation) && (
                <option value={currentLocation}>{currentLocation} (no longer offered)</option>
              )}
            </NativeSelect>
          </div>

          {isLicensing && (
            <div className="space-y-1.5">
              <Label htmlFor="inc-impound">Which pound <span className="text-destructive">*</span></Label>
              <Input id="inc-impound" value={impound} onChange={(e) => setImpound(e.target.value)} placeholder="Name of the pound or depot holding it" />
              <p className="text-[11px] text-muted-foreground">
                The car's location is set above. This names the specific pound, and is recorded on
                the incident report rather than in the location field, which is a fixed list.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="inc-moveto">Move to</Label>
            <NativeSelect id="inc-moveto" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
              <option value="">Only if it is being moved</option>
              {LOCATION_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </NativeSelect>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="inc-next">Next update date <span className="text-destructive">*</span></Label>
            <Input id="inc-next" type="date" value={nextUpdate} onChange={(e) => setNextUpdate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="inc-known">When did you become aware <span className="text-destructive">*</span></Label>
            <Input id="inc-known" type="date" value={firstKnowledge} onChange={(e) => setFirstKnowledge(e.target.value)} />
          </div>

          <div className="rounded-md border border-border px-3 py-2 sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="inc-operational" className="cursor-pointer font-normal">
                Vehicle operational
              </Label>
              <Switch id="inc-operational" checked={operational} onCheckedChange={setOperational} />
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              This sets the vehicle's operational state in the fleet register, which is what the
              weekly allocation reads.
            </p>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label>Who has been informed <span className="text-destructive">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {INFORMED_OPTIONS.map((o) => (
                <button key={o} type="button" onClick={() => toggleInformed(o)}>
                  <Chip tone={informed.includes(o) ? 'brand' : 'neutral'}>{o}</Chip>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="inc-context">Context and next steps <span className="text-destructive">*</span></Label>
            <Textarea id="inc-context" rows={2} value={context} onChange={(e) => setContext(e.target.value)} />
          </div>

          <div className="sm:col-span-2">
            <IncidentEvidence
              vehicleId={activeVehicleId}
              uploaded={files}
              onUploaded={(f) => setFiles((prev) => [...prev, f])}
              onRemove={(id) => setFiles((prev) => prev.filter((f) => f.document_id !== id))}
              links={evidence}
              onLinksChange={setEvidence}
            />
          </div>

          <div className="sm:col-span-2">
            <NotifyBox
              label="Send this report to the vehicle owner"
              checked={notify}
              onCheckedChange={setNotify}
              ownerWillBeNotified={selected?.owner_will_be_notified ?? null}
              ownerName={selected?.owner_name ?? null}
              hasVehicle={!!activeVehicleId}
              internalReason={selected?.incidents_internal_reason ?? null}
            />
          </div>
        </div>

        {missing.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Still needed: {missing.join(', ')}.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={record.loading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {record.loading && <Spinner className="h-4 w-4" />}
            Record incident
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
